import React from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import { BASE_URL } from "../../../helper/constants";
import { initialsOf } from "../cockpitFormat";

export function Card({ className = "", children, ...rest }) {
  return (
    <section className={`cockpit-card ${className}`.trim()} {...rest}>
      {children}
    </section>
  );
}

export function CardHeader({ eyebrow, title, subtitle, action, children }) {
  return (
    <div className="cockpit-card-head">
      <div className="cockpit-card-head-text">
        {eyebrow ? <div className="cockpit-eyebrow">{eyebrow}</div> : null}
        {title ? <h2 className="cockpit-card-title">{title}</h2> : null}
        {subtitle ? <div className="cockpit-card-subtitle">{subtitle}</div> : null}
      </div>
      {action || children ? <div className="cockpit-card-head-action">{action || children}</div> : null}
    </div>
  );
}

export function LinkButton({ children, onClick, icon = "arrow_forward", className = "" }) {
  return (
    <button type="button" className={`kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm ${className}`.trim()} onClick={onClick}>
      <span>{children}</span>
      {icon ? <MaterialIcon icon={icon} size={14} color="currentColor" weight={400} /> : null}
    </button>
  );
}

export function PrimaryButton({ children, onClick, icon, disabled, className = "" }) {
  return (
    <button
      type="button"
      className={`kj-btn kj-btn--primary ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
    >
      {icon ? <MaterialIcon icon={icon} size={16} color="currentColor" weight={400} /> : null}
      <span>{children}</span>
    </button>
  );
}

export function SecondaryButton({ children, onClick, icon, className = "", disabled = false }) {
  return (
    <button type="button" className={`kj-btn kj-btn--secondary ${className}`.trim()} onClick={onClick} disabled={disabled}>
      {icon ? <MaterialIcon icon={icon} size={16} color="currentColor" weight={400} /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Chip({ tone = "neutral", children }) {
  return <span className={`cockpit-chip cockpit-chip--${tone}`}>{children}</span>;
}

export function Avatar({ user, name, image, size = 28, color }) {
  const src = image || (user && user.avatar);
  const initials = initialsOf(user?.firstName, user?.lastName, name || user?.name);
  const style = {
    "--cockpit-avatar-size": `${size}px`,
    "--cockpit-avatar-fs": `${Math.max(9, Math.round(size * 0.36))}px`,
  };
  if (color) style["--cockpit-avatar-bg"] = color;
  const sizing = `cockpit-avatar--sized${color ? " cockpit-avatar--tinted" : ""}`;
  if (src) {
    return (
      <img
        className={`cockpit-avatar cockpit-avatar--photo ${sizing}`}
        src={String(src).startsWith("http") ? src : BASE_URL + src}
        alt={initials}
        style={style}
      />
    );
  }
  return (
    <span className={`cockpit-avatar ${sizing}`} style={style}>
      {initials || <MaterialIcon icon="person" size={Math.round(size * 0.6)} color="currentColor" />}
    </span>
  );
}

export function AvatarStack({ users = [], max = 3, size = 28 }) {
  const shown = users.slice(0, max);
  const rest = users.length - shown.length;
  return (
    <div className="cockpit-avatar-stack">
      {shown.map((u, i) => (
        <Avatar key={u.id || i} user={u} size={size} />
      ))}
      {rest > 0 ? (
        <span className="cockpit-avatar cockpit-avatar--sized" style={{ "--cockpit-avatar-size": `${size}px`, "--cockpit-avatar-fs": "10px" }}>
          +{rest}
        </span>
      ) : null}
    </div>
  );
}

export function EmptyState({ icon = "inbox", title, hint, action }) {
  return (
    <div className="cockpit-empty">
      <MaterialIcon icon={icon} size={28} color="#a7aeba" weight={300} />
      {title ? <div className="cockpit-empty-title">{title}</div> : null}
      {hint ? <div className="cockpit-empty-hint">{hint}</div> : null}
      {action ? <div className="cockpit-empty-action">{action}</div> : null}
    </div>
  );
}

const cssSize = (v) => (typeof v === "number" ? `${v}px` : v);

export function Skeleton({ height = 14, width = "100%", className, style }) {
  return (
    <span
      className={`cockpit-skeleton${className ? ` ${className}` : ""}`}
      style={{ "--cockpit-skeleton-h": cssSize(height), "--cockpit-skeleton-w": cssSize(width), ...style }}
    />
  );
}
